import React from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';

import { logger } from '@/utils/logger';

interface DeployDialogProps {
  open: boolean;
  onClose: () => void;
  onDeploy: () => Promise<void>;
  environmentName: string;
}

/**
 * Dialog to confirm recipe deployment to environment
 */
export const DeployDialog: React.FC<DeployDialogProps> = ({
  open,
  onClose,
  onDeploy,
  environmentName,
}) => {
  const [isDeploying, setIsDeploying] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleDeploy = async () => {
    try {
      setIsDeploying(true);
      setError(null);
      await onDeploy();
      onClose();
    } catch (err) {
      logger.error('Failed to deploy:', err);
      setError(err instanceof Error ? err.message : 'Deployment failed. Please try again.');
    } finally {
      setIsDeploying(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Deploy to {environmentName}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to deploy this recipe to <strong>{environmentName}</strong>?
        </DialogContentText>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Deployed recipes can subscribe to events.
        </Typography>
        <Typography variant="caption" color="warning.main" sx={{ mt: 2, display: 'block' }}>
          * Create a new version to continue editing after deploying
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={handleClose} disabled={isDeploying}>
          Cancel
        </Button>
        <Button onClick={handleDeploy} variant="contained" disabled={isDeploying}>
          {isDeploying ? 'Deploying...' : 'Deploy'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
